import React from 'react';

export type CommissionTier = {
    min_amount: number;
    max_amount: number | null;
    commission_rate: number;
    max_amount_inclusive?: boolean;
};

type CommissionTierMap = Record<string, CommissionTier[] | undefined>;

interface CommissionSummaryTableProps {
    currency: string;
    depositRate: number;
    withdrawRate: number;
    depositTiers?: CommissionTier[];
    withdrawTiers?: CommissionTier[];
    depositMethodTiers?: CommissionTierMap;
    withdrawMethodTiers?: CommissionTierMap;
    depositMethods: string[];
    withdrawMethods: string[];
    methodLabel: (method: string) => string;
}

const amountLabel = (amount: number | null, currency: string) => {
    if (amount === null) return '∞';
    return `${amount.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${currency}`;
};

const CommissionValue: React.FC<{
    rate: number;
    tiers?: CommissionTier[];
    currency: string;
}> = ({ rate, tiers, currency }) => {
    if (!tiers?.length) {
        return <span className="font-semibold text-slate-900 dark:text-slate-100">{rate}%</span>;
    }

    if (tiers.length === 1 && tiers[0].min_amount === 0 && tiers[0].max_amount === null) {
        return <span className="font-semibold text-slate-900 dark:text-slate-100">{tiers[0].commission_rate}%</span>;
    }

    return (
        <div className="flex flex-col gap-1">
            {tiers.map((tier, index) => (
                <span key={index} className="whitespace-nowrap text-xs text-slate-700 dark:text-slate-200">
                    {amountLabel(tier.min_amount, currency)}–{amountLabel(tier.max_amount, currency)}{tier.max_amount !== null && tier.max_amount_inclusive ? ' включ.' : ''}: <b>{tier.commission_rate}%</b>
                </span>
            ))}
        </div>
    );
};

export const CommissionSummaryTable: React.FC<CommissionSummaryTableProps> = ({
    currency,
    depositRate,
    withdrawRate,
    depositTiers,
    withdrawTiers,
    depositMethodTiers = {},
    withdrawMethodTiers = {},
    depositMethods,
    withdrawMethods,
    methodLabel,
}) => {
    const depositMethodSet = new Set(depositMethods);
    const withdrawMethodSet = new Set(withdrawMethods);
    const availableMethods = Array.from(new Set([...depositMethods, ...withdrawMethods]));
    const configuredMethods = availableMethods.filter((method) => (
        Boolean(depositMethodTiers[method]?.length)
        || Boolean(withdrawMethodTiers[method]?.length)
    ));
    const hasNoKnownMethods = availableMethods.length === 0;
    const hasDepositFallbackMethods = depositMethods.some((method) => !depositMethodTiers[method]?.length);
    const hasWithdrawFallbackMethods = withdrawMethods.some((method) => !withdrawMethodTiers[method]?.length);
    const showFallbackRow = hasNoKnownMethods || hasDepositFallbackMethods || hasWithdrawFallbackMethods;

    return (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
            <table className="w-full min-w-[460px] text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-500 dark:text-slate-400">
                    <tr>
                        <th className="px-3 py-2 text-left font-medium">Метод</th>
                        <th className="px-3 py-2 text-left font-medium">Пополнение</th>
                        <th className="px-3 py-2 text-left font-medium">Вывод</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700 bg-white dark:bg-slate-800">
                    {configuredMethods.map((method) => (
                        <tr key={method}>
                            <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-100">{methodLabel(method)}</td>
                            <td className="px-3 py-2 align-top">
                                {depositMethodSet.has(method) ? (
                                    <CommissionValue
                                        rate={depositRate}
                                        tiers={depositMethodTiers[method]?.length ? depositMethodTiers[method] : depositTiers}
                                        currency={currency}
                                    />
                                ) : '—'}
                            </td>
                            <td className="px-3 py-2 align-top">
                                {withdrawMethodSet.has(method) ? (
                                    <CommissionValue
                                        rate={withdrawRate}
                                        tiers={withdrawMethodTiers[method]?.length ? withdrawMethodTiers[method] : withdrawTiers}
                                        currency={currency}
                                    />
                                ) : '—'}
                            </td>
                        </tr>
                    ))}
                    {showFallbackRow && (
                        <tr>
                            <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-100">
                                {configuredMethods.length ? 'Все остальные' : 'Все методы'}
                            </td>
                            <td className="px-3 py-2 align-top">
                                {hasNoKnownMethods || hasDepositFallbackMethods
                                    ? <CommissionValue rate={depositRate} tiers={depositTiers} currency={currency} />
                                    : '—'}
                            </td>
                            <td className="px-3 py-2 align-top">
                                {hasNoKnownMethods || hasWithdrawFallbackMethods
                                    ? <CommissionValue rate={withdrawRate} tiers={withdrawTiers} currency={currency} />
                                    : '—'}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};
